// Cambio obligatorio de contraseña (ver informe "LYNX — Fase 1.2.9"):
// única pantalla a la que puede navegar un usuario cuya contraseña vigente
// no cumple la política actual (ver AuthContext.mustChangePassword,
// calculado en el login — server/auth/auth.js). El propio ProtectedRoute
// es quien redirige para acá y bloquea cualquier otra ruta mientras esto
// no se resuelva — esta pantalla no se autoguarda en ningún guard.
//
// Tras guardar: se cierra la sesión y se vuelve a Login. Se evaluó reemitir
// un token nuevo acá mismo (evitaría el segundo login manual), pero eso
// significa mezclar lógica de emisión de JWT (hoy exclusiva de
// server/auth/auth.js) dentro de current/users.js — para una pantalla que
// se usa una vez en la vida útil de una contraseña, no se justifica esa
// complejidad extra. Forzar un login real además prueba que la contraseña
// nueva quedó bien guardada, no solo que el formulario la aceptó.
import React, { useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import PasswordInput from "../components/ui/PasswordInput";
import Toast, { ToastMessage } from "../components/ui/Toast";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { validatePassword } from "../utils/passwordPolicy";

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  background: "var(--background)",
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "440px",
  padding: "32px",
};

const fieldWrapperStyle: React.CSSProperties = { marginBottom: "18px" };
const labelStyle: React.CSSProperties = { display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px", color: "var(--text-primary)" };
const errorStyle: React.CSSProperties = {
  marginBottom: "18px",
  padding: "12px 14px",
  borderRadius: "14px",
  border: "1px solid rgba(201,107,99,0.24)",
  background: "rgba(201,107,99,0.10)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

export default function ForcedPasswordChange() {
  const { token, userId, username, logout } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!newPassword || !confirmPassword) {
      setError("Completá ambos campos.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    const policyResult = validatePassword(newPassword);
    if (!policyResult.valid) {
      setError(policyResult.message);
      return;
    }

    setSaving(true);
    try {
      const res = await authFetch(`/v2/users/${userId}/change-password`, {
        method: "PUT",
        token,
        json: true,
        body: JSON.stringify({ password: newPassword }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo actualizar la contraseña");
      }

      // Toast en vez de alert() (ver informe "LYNX — Fase 1.6, último ajuste
      // antes del commit"): como el logout es lo que efectivamente saca a la
      // persona de esta pantalla (vía ProtectedRoute, al dejar de estar
      // autenticada), se demora un instante para que el toast alcance a
      // leerse antes de que la pantalla se desmonte — con alert() ese tiempo
      // lo daba el propio diálogo bloqueante, ahora hay que dárselo a mano.
      setToast({ variant: "success", title: "Contraseña actualizada correctamente", detail: "Iniciá sesión de nuevo con tu nueva contraseña." });
      setTimeout(logout, 2000);
      // saving queda en true a propósito: el botón se mantiene deshabilitado
      // durante esos 2 segundos, hasta que el logout se dispare.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error actualizando la contraseña");
      setSaving(false);
    }
  };

  return (
    <div style={pageStyle}>
      <Toast message={toast} onClose={() => setToast(null)} />
      <Card style={cardStyle}>
        <h2 style={{ margin: "0 0 8px 0", fontSize: "24px", fontWeight: 800, color: "var(--text-primary)" }}>
          Actualizá tu contraseña
        </h2>
        <p style={{ margin: "0 0 24px 0", color: "var(--text-secondary)", fontSize: "14px", lineHeight: 1.5 }}>
          {username ? `Hola ${username}, tu` : "Tu"} contraseña actual no cumple con la política de seguridad vigente.
          Definí una nueva antes de continuar — no vas a poder usar el sistema hasta hacerlo.
        </p>

        <form onSubmit={handleSubmit}>
          {error && <div style={errorStyle}>❌ {error}</div>}

          <div style={fieldWrapperStyle}>
            <label style={labelStyle}>Nueva contraseña</label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError(null);
              }}
              placeholder="Ingresá tu nueva contraseña"
              autoComplete="new-password"
            />
          </div>

          <div style={fieldWrapperStyle}>
            <label style={labelStyle}>Confirmar contraseña</label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError(null);
              }}
              placeholder="Repetí tu nueva contraseña"
              autoComplete="new-password"
            />
          </div>

          <div style={{ marginBottom: "18px", fontSize: "12px", color: "var(--text-secondary)" }}>
            Debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número.
          </div>

          <Button type="submit" fullWidth disabled={saving}>
            {saving ? "Guardando…" : "Guardar y continuar"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
